import type { CurrentUser } from '@core/models/current-user.model';
import type { StoredAuthToken } from '@core/models/stored-auth-token.model';
import type { UserTenant } from '@core/models/user-tenant.model';
import { coerceIndustryType } from '@core/utils/industry.util';

export interface AuthSelectTenantUser {
  id: string;
  fullName?: string;
  email: string;
  role: string;
}

export interface AuthSelectTenantInfo {
  tenantId: string;
  tenantName: string;
  /** Numeric industry from API (`IndustryType`). */
  industry?: number | null;
  logo?: string;
  /** API may return logo as `logoBase64` instead of `logo`. */
  logoBase64?: string | null;
  qrToken: string;
  /** Tenant's active currency, used to format amounts app-wide. */
  currencyId?: number | null;
  currencyCode?: string;
  currencyName?: string;
  currencySymbol?: string;
}

export interface AuthSelectTenantResponse {
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresAt: string;
  refreshTokenExpiresAt: string;
  user: AuthSelectTenantUser;
  /** Omitted for super-admin select-tenant (no tenant context). */
  currentTenant?: AuthSelectTenantInfo;
  tenants?: AuthSelectTenantInfo[];
}

function resolveTenantLogo(info: AuthSelectTenantInfo): string {
  return (info.logoBase64 ?? info.logo ?? '').trim();
}

function mapTenantInfo(info: AuthSelectTenantInfo): UserTenant {
  return {
    tenantId: info.tenantId,
    tenantName: info.tenantName,
    logo: resolveTenantLogo(info),
    qrToken: info.qrToken,
    industry: coerceIndustryType(info.industry),
    currencyId: info.currencyId ?? null,
    currencyCode: info.currencyCode,
    currencyName: info.currencyName,
    currencySymbol: info.currencySymbol,
  };
}

export function mapAuthSelectTenantTenants(res: AuthSelectTenantResponse): UserTenant[] {
  return (res.tenants ?? [])
    .map((item) => mapTenantInfo(item))
    .filter((tenant) => tenant.tenantId.length > 0);
}

export function mapAuthSelectTenantResponse(
  res: AuthSelectTenantResponse,
  remember: boolean,
): { token: StoredAuthToken; user: CurrentUser; tenants: UserTenant[] } {
  const fullName = (res.user.fullName ?? '').trim();
  const currentTenant = res.currentTenant;
  const industry = currentTenant ? coerceIndustryType(currentTenant.industry) : null;

  const user: CurrentUser = {
    id: res.user.id,
    ...(fullName ? { fullName } : {}),
    email: res.user.email,
    role: res.user.role,
    ...(currentTenant
      ? {
          tenantId: currentTenant.tenantId,
          tenantName: currentTenant.tenantName,
          logo: resolveTenantLogo(currentTenant),
          industry,
          qrToken: currentTenant.qrToken,
          currencyId: currentTenant.currencyId ?? null,
          currencyCode: currentTenant.currencyCode,
          currencyName: currentTenant.currencyName,
          currencySymbol: currentTenant.currencySymbol,
        }
      : {}),
  };

  return {
    token: {
      accessToken: res.accessToken,
      refreshToken: res.refreshToken,
      expiresAt: res.accessTokenExpiresAt,
      refreshExpiresAt: res.refreshTokenExpiresAt,
      remember,
    },
    user,
    tenants: mapAuthSelectTenantTenants(res),
  };
}
